// ================================================================
// src/pages/TeacherPrivateClassPage.jsx
// نسخه‌ی نهایی یکپارچه — State Machine + Race Lock + Batch Notify
// ================================================================

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from 'react';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './PrivateClassPage.css';

import {
  useTheme,
  useToast,
  useData,
  useScrollProgress,
  useSearchShortcut,
  gradeOptions,
  fieldOptions,
  getSubjects,
  capacityOptions,
  Modal,
  PersianCalendar,
  PersianDatePicker,
  RatingStars,
  Confetti,
  PageParticles,
  DEFAULT_TEACHER_REQUEST_FORM,
  useRoomeetAuthGate,
  AuthGate,
  PrivateClassProviders,
  COURSE_STATUS,
  canStartCourse,
  getCourseStartMoment,
  getCourseStatusLabel,
  VAT_RATE,
  WithdrawModal,
} from './PrivateClassShared';
import { needsField } from '../core/education/curriculum.js';

// ================================================================
// پنل معلم
// ================================================================
function TeacherPanel({
  user,
  state,
  dispatch,
  refreshData,
  showToast,
  internalApi,
  roomeetApi,
  loading,
  isRoomeetReady,
}) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [form, setForm] = useState({ ...DEFAULT_TEACHER_REQUEST_FORM });
  const [editingRequest, setEditingRequest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [confetti, setConfetti] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStartingClass, setIsStartingClass] = useState(false);
  const [isEndingClass, setIsEndingClass] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);

  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [selectedCourseStudents, setSelectedCourseStudents] = useState([]);
  const [selectedCourseTitle, setSelectedCourseTitle] = useState('');

  // 🕒 تیک هر ثانیه برای countdown زنده
  const [nowMoment, setNowMoment] = useState(moment());
  useEffect(() => {
    const interval = setInterval(() => setNowMoment(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const requests = useMemo(
    () => state.requests.filter((r) => r.teacherId === user.id),
    [state.requests, user.id]
  );
  const teacherCourses = useMemo(
  () =>
    state.courses.filter(
      (c) =>
        c.teacherId === user.id &&
        !(c.hiddenByUsers || []).includes(user.id)
    ),
  [state.courses, user.id]
);
  const userData = state.users.find((u) => u.id === user.id);
  const { enableTeacherRegistration } = state.adminSettings;

  const totalPages = Math.ceil(requests.length / perPage);
  const paginatedRequests = requests.slice().reverse().slice((page - 1) * perPage, page * perPage);

  const availableFields = useMemo(
    () => (needsField(form.grade) ? fieldOptions : []),
    [form.grade]
  );
  const availableSubjects = useMemo(
    () => getSubjects(form.grade, form.field),
    [form.grade, form.field]
  );

  // ============================================================
  // کلاس زنده و کلاس بعدی
  // ============================================================
  const liveClass = useMemo(
    () => teacherCourses.find((c) => c.status === COURSE_STATUS.LIVE),
    [teacherCourses]
  );

  const nextScheduledClass = useMemo(() => {
    return teacherCourses
      .filter((c) => c.status === COURSE_STATUS.SCHEDULED && c.siroomehClassId)
      .filter((c) => {
        const startMoment = getCourseStartMoment(c);
        if (!startMoment) return false;
        return nowMoment.diff(startMoment, 'hours') < 3;
      })
      .sort((a, b) => {
        const am = getCourseStartMoment(a)?.valueOf() || 0;
        const bm = getCourseStartMoment(b)?.valueOf() || 0;
        return am - bm;
      })[0];
  }, [teacherCourses, nowMoment]);

  const featuredClass = liveClass || nextScheduledClass;

  // ============================================================
  // فرم ثبت درخواست
  // ============================================================
  useEffect(() => {
    if (!needsField(form.grade)) {
      setForm((p) => ({ ...p, field: '', subject: '' }));
    } else {
      setForm((p) => ({ ...p, field: availableFields[0] || '', subject: '' }));
    }
  }, [form.grade, availableFields]);

  useEffect(() => {
    setForm((p) => ({ ...p, subject: '' }));
  }, [form.field]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // ============================================================
  // helper: batch notification
  // ============================================================
  const sendBatchNotifications = async (notifications) => {
    if (!notifications || notifications.length === 0) return;
    const data = await internalApi._fetchData(true);
    const now = moment().format('jYYYY-jMM-jDD');
    for (const n of notifications) {
      data.notifications.push({
        id: Date.now() + Math.random() + Math.random(),
        message: n.message,
        type: n.type || 'info',
        read: false,
        date: now,
        userId: n.userId || null,
      });
    }
    await internalApi._saveData(data);
  };

  // ============================================================
  // ثبت درخواست جدید / ویرایش
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!enableTeacherRegistration) {
      showToast('ثبت‌نام معلمان در حال حاضر توسط ادمین غیرفعال شده است.', 'error');
      return;
    }
    if (isSubmitting) return;
    if (!form.title || !form.date || !form.time || !form.subject) {
      showToast('لطفاً عنوان، تاریخ، ساعت و درس را وارد کنید', 'error');
      return;
    }
    const conflict = teacherCourses.some(
      (c) =>
        c.date === form.date &&
        c.time === form.time &&
        c.status !== COURSE_STATUS.CANCELLED &&
        c.status !== COURSE_STATUS.ENDED &&
        (!editingRequest || c.id !== editingRequest.courseId)
    );
    if (conflict) {
      showToast('شما در این زمان کلاس دیگری دارید', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      if (editingRequest) {
        await internalApi.updateRequest(editingRequest.id, form);
        showToast('درخواست با موفقیت ویرایش شد', 'success');
        setEditingRequest(null);
      } else {
        await internalApi.addRequest({ ...form, teacherId: user.id });

        // ✅ E25: notify فقط به ادمین‌ها (نه broadcast)
        const data = await internalApi._fetchData(true);
        const admins = data.users.filter((u) => u.role === 'admin');
        const notifs = admins.map((admin) => ({
          message: `📋 درخواست کلاس جدید از ${user.name}: ${form.title}`,
          type: 'info',
          userId: admin.id,
        }));
        await sendBatchNotifications(notifs);

        // Sync معلم با Roomeet در پس‌زمینه
        (async () => {
          try { await internalApi._ensureWithAdmin(user.id); } catch (_) {}
        })();

        showToast('درخواست شما ثبت شد و در انتظار تأیید است', 'success');
      }
      setForm({ ...DEFAULT_TEACHER_REQUEST_FORM });
      await refreshData();
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (req) => {
    setEditingRequest(req);
    setForm(req);
    setActiveTab('new-request');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این درخواست مطمئن هستید؟')) return;
    try {
      await internalApi.deleteRequest(id);
      await refreshData();
      showToast('درخواست حذف شد', 'info');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // لغو کلاس (فقط scheduled)
  // ============================================================
  const handleCancelTeacherCourse = async (courseId) => {
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (course.status !== COURSE_STATUS.SCHEDULED) {
      showToast('فقط کلاس‌های برنامه‌ریزی‌شده قابل لغو هستند', 'error');
      return;
    }
    if (
      !window.confirm(
        'آیا از لغو این کلاس مطمئن هستید؟ وجه دانش‌آموزان برگشت داده می‌شود.'
      )
    )
      return;

    // 🆕 قفل مشترک با enrollStudent برای جلوگیری از race
    const LOCK_KEY = `roomeet_enroll_lock_${courseId}`;
    try {
      const now = Date.now();
      const last = Number(localStorage.getItem(LOCK_KEY) || 0);
      if (now - last < 8000) {
        showToast('در همین لحظه یک عملیات دیگر روی این کلاس در حال انجام است. لطفاً چند لحظه بعد دوباره تلاش کنید.', 'warning');
        return;
      }
      localStorage.setItem(LOCK_KEY, String(now));
    } catch (_) { /* در صورت خطای localStorage، ادامه بده */ }

    try {
      await internalApi.cancelCourse(courseId, 'لغو توسط معلم');

      // ✅ batch notification
      const notifs = [
        {
          message: `❌ کلاس "${course.title}" توسط معلم لغو شد.`,
          type: 'warning',
          userId: course.teacherId,
        },
      ];
      for (const s of (course.enrolledStudents || [])) {
        notifs.push({
          message: `❌ کلاس "${course.title}" توسط معلم لغو شد و وجه شما بازگشت.`,
          type: 'warning',
          userId: s.userId,
        });
      }
      // ✅ E38: notify ادمین‌ها
      const data = await internalApi._fetchData(true);
      const admins = data.users.filter((u) => u.role === 'admin');
      for (const admin of admins) {
        notifs.push({
          message: `❌ کلاس "${course.title}" توسط معلم ${user.name} لغو شد.`,
          type: 'info',
          userId: admin.id,
        });
      }
      await sendBatchNotifications(notifs);

      await refreshData();
      showToast('کلاس لغو شد و وجه دانش‌آموزان بازگشت', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    } finally {
      // 🆕 رهاسازی قفل با تأخیر کوچک
      setTimeout(() => {
        try { localStorage.removeItem(LOCK_KEY); } catch (_) {}
      }, 2000);
    }
  };

  // ============================================================
  // ورود معلم به کلاس (ادامه کلاس)
  // ============================================================
  const handleJoinClass = async (courseId) => {
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (!course.siroomehClassId) {
      showToast('کلاس هنوز در رومیت ایجاد نشده است', 'warning');
      return;
    }
    if (course.status === COURSE_STATUS.CANCELLED) {
      showToast('این کلاس لغو شده است', 'error');
      return;
    }
    if (course.status !== COURSE_STATUS.LIVE) {
      showToast('کلاس هنوز شروع نشده است', 'warning');
      return;
    }

    try {
      const token = user.roomeetToken || roomeetApi.token;
      const schoolId = user.schoolId || roomeetApi.schoolId;
      if (!token) {
        showToast('توکن رومیت یافت نشد. لطفاً دوباره وارد شوید.', 'error');
        return;
      }
      roomeetApi.setAuth(token, schoolId, user.id, 'teacher');

      const result = await internalApi.joinCourse(courseId);
      if (result?.link) {
        window.open(result.link, '_blank');
      } else {
        showToast('لینک ورود دریافت نشد', 'error');
      }
    } catch (e) {
      console.error('[TeacherJoin] خطا:', e);
      showToast(`خطا در ورود: ${e.message}`, 'error');
    }
  };

  // ============================================================
  // ✅ شروع کلاس — با متد یکپارچه startCourse + Lock
  // ============================================================
  const handleStartClass = async (courseId) => {
    if (isStartingClass) return;
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (!canStartCourse(course, moment())) {
      showToast('هنوز زمان شروع کلاس نرسیده است', 'warning');
      return;
    }
    if (!window.confirm('آیا از شروع این کلاس مطمئن هستید؟')) return;
    setIsStartingClass(true);

    // تب خالی را همین اول باز کن (popup blocker)
    const newTab = window.open('about:blank', '_blank');

    try {
      const token = user.roomeetToken || roomeetApi.token;
      const schoolId = user.schoolId || roomeetApi.schoolId;
      if (!token) {
        if (newTab && !newTab.closed) newTab.close();
        showToast('لطفاً ابتدا وارد رومیت شوید.', 'error');
        return;
      }
      roomeetApi.setAuth(token, schoolId, user.id, 'teacher');

      // ✅ شروع کلاس با متد یکپارچه (خودش lock + release payment رو مدیریت می‌کنه)
      const result = await internalApi.startCourse(courseId, 'teacher');
      if (!result?.success) throw new Error('شروع کلاس ناموفق بود');

      // ✅ لینک اختصاصی معلم
      const join = await internalApi.joinCourse(courseId);
      if (!join?.link) throw new Error('لینک ورود معلم دریافت نشد');

      if (newTab && !newTab.closed) {
        newTab.location.href = join.link;
      } else {
        window.open(join.link, '_blank');
      }

      await refreshData();
      showToast('✅ کلاس شروع شد — در تب جدید وارد کلاس شدید', 'success');
    } catch (error) {
      if (newTab && !newTab.closed) newTab.close();
      showToast(`❌ خطا در شروع کلاس: ${error.message}`, 'error');
    } finally {
      setIsStartingClass(false);
    }
  };

  // ============================================================
  // ✅ اتمام کلاس — با متد یکپارچه endCourse
  // ============================================================
  const handleEndClass = async (courseId) => {
    if (isEndingClass) return;
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (course.status !== COURSE_STATUS.LIVE) {
      showToast('این کلاس در حال برگزاری نیست', 'warning');
      return;
    }
    if (
      !window.confirm(
        'آیا از اتمام این کلاس مطمئن هستید؟ کلاس برای همه بسته می‌شود.'
      )
    )
      return;
    setIsEndingClass(true);
    try {
      await internalApi.endCourse(courseId, 'teacher', 'manual');
      await refreshData();
      showToast('✅ کلاس پایان یافت', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    } finally {
      setIsEndingClass(false);
    }
  };

  // ============================================================
  // برداشت از کیف پول
  // ============================================================
  const handleWithdrawSubmit = async ({ tomanAmount, fullName, cardNumber, shaba }) => {
    setWithdrawModalOpen(false);
    try {
      // ذخیره‌ی اطلاعات بانکی در پروفایل کاربر برای دفعه‌ی بعد
      await internalApi.updateUser(user.id, {
        wallet: (userData?.wallet || 0) - tomanAmount,
        bankingInfo: { fullName, cardNumber, shaba },
      });

      // ثبت تراکنش برداشت با تمام اطلاعات بانکی و وضعیت pending
      await internalApi.addTransaction({
        id: `wd_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        userId: user.id,
        amount: tomanAmount,
        date: moment().format('jYYYY-jMM-jDD'),
        type: 'withdraw',
        status: 'pending',
        fullName,
        cardNumber,
        shaba,
        requestedAt: moment().format('jYYYY-jMM-jDD HH:mm'),
      });

      // اطلاع به ادمین‌ها
      const data = await internalApi._fetchData(true);
      const admins = data.users.filter((u) => u.role === 'admin');
      if (admins.length) {
        const notifs = admins.map((admin) => ({
          message: `💸 درخواست برداشت جدید از ${user.name}: ${tomanAmount.toLocaleString('fa-IR')} تومان`,
          type: 'info',
          userId: admin.id,
        }));
        const now = moment().format('jYYYY-jMM-jDD');
        for (const n of notifs) {
          data.notifications.push({
            id: Date.now() + Math.random(),
            message: n.message, type: n.type, read: false, date: now, userId: n.userId,
          });
        }
        await internalApi._saveData(data);
      }

      await refreshData();
      showToast(`✅ درخواست برداشت ${tomanAmount.toLocaleString('fa-IR')} تومان ثبت شد. منتظر تایید ادمین باشید.`, 'success', 6000);
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

    // 🆕 حذف/بایگانی درخواست برداشت توسط معلم (فقط بعد از تکمیل یا رد)
  const handleArchiveWithdrawal = async (withdrawalId) => {
    if (!window.confirm('این درخواست از لیست شما حذف شود؟ (برای ادمین همچنان قابل مشاهده است)')) return;
    try {
      await internalApi.updateTransaction(withdrawalId, {
        deletedByTeacher: true,
        deletedAt: moment().format('jYYYY-jMM-jDD HH:mm'),
      });
      await refreshData();
      showToast('درخواست از لیست شما حذف شد', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const tabs = [
    { key: 'dashboard', icon: 'fa-th-large', label: 'خلاصه' },
    { key: 'requests', icon: 'fa-list-ul', label: 'درخواست‌ها' },
    { key: 'new-request', icon: 'fa-plus-circle', label: 'ثبت کلاس' },
    { key: 'my-classes', icon: 'fa-book-open', label: 'کلاس‌های من' },
    { key: 'calendar', icon: 'fa-calendar-alt', label: 'تقویم' },
    { key: 'wallet', icon: 'fa-wallet', label: 'کیف پول' },
    { key: 'withdrawals', icon: 'fa-hand-holding-usd', label: 'برداشت‌های من' }, // 🆕
  ];






  const handleHideCourse = async (courseId) => {
  if (!window.confirm('این کلاس از لیست شما حذف شود؟')) return;
  try {
    await internalApi.hideCourseForUser(courseId, user.id);
    await refreshData();
    showToast('کلاس از لیست حذف شد', 'success');
  } catch (e) {
    showToast(`خطا: ${e.message}`, 'error');
  }
};





  // ============================================================
  // رندر دکمه‌های کلاس بر اساس وضعیت
  // ============================================================
  const renderCourseActions = (c) => {
    const startable = canStartCourse(c, nowMoment);

    // لغو شده
    if (c.status === COURSE_STATUS.CANCELLED) {
      return (
        <span className="btn-disabled" style={{ color: '#f43f5e' }}>
          ❌ لغو شده
        </span>
      );
    }

    // پایان یافته
    if (c.status === COURSE_STATUS.ENDED) {
  return (
    <>
      <span className="btn-disabled">✅ کلاس تمام شد</span>
      <button
        className="icon-btn danger"
        onClick={() => handleHideCourse(c.id)}
        title="حذف از لیست"
      >
        <i className="fas fa-trash-alt" />
      </button>
    </>
  );
}

    // در حال برگزاری
    if (c.status === COURSE_STATUS.LIVE) {
      return (
        <>
          <button
            className="btn-primary btn-sm pc-btn-glow"
            onClick={() => handleJoinClass(c.id)}
          >
            <i className="fas fa-video" /> ادامه کلاس
          </button>
          <button
            className="btn-outline btn-sm"
            onClick={() => handleEndClass(c.id)}
            disabled={isEndingClass}
            style={{ color: '#f43f5e', borderColor: '#f43f5e' }}
          >
            <i className="fas fa-stop" /> اتمام کلاس
          </button>
        </>
      );
    }

    // scheduled — زمان شروع رسیده
    if (startable) {
      return (
        <>
          <button
            className="btn-primary btn-sm pc-btn-glow"
            onClick={() => handleStartClass(c.id)}
            disabled={isStartingClass}
          >
            <i className="fas fa-play" /> شروع کلاس
          </button>
          <button
            className="btn-outline btn-sm"
            onClick={() => handleCancelTeacherCourse(c.id)}
          >
            <i className="fas fa-ban" /> لغو
          </button>
        </>
      );
    }

    // scheduled — منتظر زمان
    return (
      <>
        <span className="btn-disabled">
          <i className="fas fa-clock" /> {getCourseStatusLabel(c, nowMoment)}
        </span>
        <button
          className="btn-outline btn-sm"
          onClick={() => handleCancelTeacherCourse(c.id)}
        >
          <i className="fas fa-ban" /> لغو
        </button>
      </>
    );
  };

  // ============================================================
  // رندر محتوا
  // ============================================================
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <>
            {confetti && <Confetti />}
            <div className="stats-grid">
              <div className="stat-card glass pc-animate-slide-up">
                <div className="stat-icon"><i className="fas fa-video" /></div>
                <div className="stat-content">
                  <div className="label">کلاس‌های فعال</div>
                  <div className="value">
                    {teacherCourses.filter(
                      (c) =>
                        c.status === COURSE_STATUS.SCHEDULED ||
                        c.status === COURSE_STATUS.LIVE
                    ).length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-1">
                <div className="stat-icon"><i className="fas fa-clock" /></div>
                <div className="stat-content">
                  <div className="label">در انتظار تأیید</div>
                  <div className="value">
                    {requests.filter((r) => r.status === 'pending').length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-2">
                <div className="stat-icon"><i className="fas fa-user-graduate" /></div>
                <div className="stat-content">
                  <div className="label">دانش‌آموزان</div>
                  <div className="value">
                    {teacherCourses
                      .filter((c) => c.status !== COURSE_STATUS.CANCELLED)
                      .reduce((acc, c) => acc + (c.enrolled || 0), 0)}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-3">
                <div className="stat-icon"><i className="fas fa-coins" /></div>
                <div className="stat-content">
                  <div className="label">اعتبار قابل برداشت</div>
                  <div className="value">
                    {(userData?.wallet || 0).toLocaleString('fa-IR')} تومان
                  </div>
                </div>
              </div>
            </div>

            <div className="dashboard-grid">
              {/* فعالیت اخیر */}
              <div className="card glass pc-animate-fade-in">
                <div className="card-header">
                  <h3><i className="fas fa-history" /> فعالیت اخیر</h3>
                </div>
                <div className="activity-list pc-stagger-enter">
                  {requests.slice(0, 3).map((r) => (
                    <div key={r.id} className="activity-item pc-animate-slide-up">
                      <div
                        className={`activity-icon ${
                          r.status === 'approved'
                            ? 'success'
                            : r.status === 'pending'
                            ? 'warning'
                            : ''
                        }`}
                      >
                        <i
                          className={
                            r.status === 'approved'
                              ? 'fas fa-check'
                              : r.status === 'pending'
                              ? 'fas fa-clock'
                              : 'fas fa-times'
                          }
                        />
                      </div>
                      <div className="activity-text">
                        {r.title} –{' '}
                        {r.status === 'approved'
                          ? 'تأیید شد'
                          : r.status === 'pending'
                          ? 'در انتظار'
                          : 'رد شد'}
                      </div>
                      <span className="time">{r.date}</span>
                    </div>
                  ))}
                  {requests.length === 0 && (
                    <p className="empty-state">هیچ فعالیتی ثبت نشده است</p>
                  )}
                </div>
              </div>

              {/* کلاس جاری / بعدی */}
              <div className="card glass pc-animate-fade-in pc-delay-1 next-class-card">
                <div className="card-header">
                  <h3>
                    <i className="fas fa-hourglass-half" />{' '}
                    {liveClass ? 'کلاس جاری' : 'کلاس بعدی'}
                  </h3>
                </div>
                <div className="next-class-content">
                  {featuredClass ? (
                    <>
                      <div className="class-title">{featuredClass.title}</div>
                      <div className="class-datetime">
                        <i className="fas fa-calendar-alt" />{' '}
                        {moment(featuredClass.date, 'jYYYY-jMM-jDD').format('jD jMMMM')} –{' '}
                        {featuredClass.time}
                        <span style={{ marginLeft: '0.5rem' }}>
                          | مدت: {featuredClass.duration || 60} دقیقه
                        </span>
                      </div>

                      {featuredClass.status === COURSE_STATUS.LIVE ? (
                        <div className="timer-circle" style={{ color: '#22c55e' }}>
                          🟢 در حال برگزاری
                        </div>
                      ) : (
                        <div className="timer-circle">
                          {getCourseStatusLabel(featuredClass, nowMoment)}
                        </div>
                      )}

                      <div
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {featuredClass.status === COURSE_STATUS.LIVE && (
                          <>
                            <button
                              className="btn-primary btn-glow"
                              onClick={() => handleJoinClass(featuredClass.id)}
                            >
                              <i className="fas fa-video" /> ادامه کلاس
                            </button>
                            <button
                              className="btn-outline btn-sm"
                              onClick={() => handleEndClass(featuredClass.id)}
                              disabled={isEndingClass}
                              style={{ color: '#f43f5e', borderColor: '#f43f5e' }}
                            >
                              <i className="fas fa-stop" /> اتمام کلاس
                            </button>
                          </>
                        )}
                        {featuredClass.status === COURSE_STATUS.SCHEDULED && (
                          <>
                            {canStartCourse(featuredClass, nowMoment) ? (
                              <button
                                className="btn-primary btn-glow"
                                onClick={() => handleStartClass(featuredClass.id)}
                                disabled={isStartingClass}
                              >
                                <i className="fas fa-play" /> شروع کلاس
                              </button>
                            ) : (
                              <button className="btn-outline" disabled>
                                <i className="fas fa-clock" /> منتظر زمان شروع
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </>
                  ) : (
                    <p className="empty-state">هیچ کلاس آینده‌ای ندارید</p>
                  )}
                </div>
              </div>
            </div>
          </>
        );

      case 'requests':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-list-ul" /> درخواست‌های کلاس</h3>
            </div>
            <div className="request-list pc-stagger-enter">
              {paginatedRequests.map((req) => (
                <div key={req.id} className="request-item pc-animate-slide-up">
                  <div className="request-info">
                    <h4>{req.title}</h4>
                    <p>
                      <i className="fas fa-calendar-alt" /> {req.date} –{' '}
                      <i className="fas fa-clock" /> {req.time}{' '}
                      <span style={{ color: 'var(--pc-text-muted)' }}>
                        | مدت: {req.duration || 60} دقیقه
                      </span>
                    </p>
                    <p>
                      <i className="fas fa-tag" />{' '}
                      {(req.price || 0).toLocaleString('fa-IR')} تومان – ظرفیت {req.capacity}
                    </p>
                    {req.errorMessage && (
                      <p style={{ color: '#f43f5e', fontSize: '0.85rem' }}>
                        <i className="fas fa-exclamation-triangle" /> {req.errorMessage}
                      </p>
                    )}
                  </div>
                  <span className={`status-badge ${req.status}`}>
                    {req.status === 'approved'
                      ? '✓ تأیید شده'
                      : req.status === 'pending'
                      ? '⏳ در انتظار'
                      : req.status === 'failed'
                      ? '❌ ناموفق'
                      : req.status === 'deleted'
                      ? '🗑️ حذف شده'
                      : '✕ رد شده'}
                  </span>
                  <div className="request-actions">
                    {req.status === 'pending' && (
                      <>
                        <button className="icon-btn" onClick={() => handleEdit(req)}>
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => handleDelete(req.id)}
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </>
                    )}
                    {req.status === 'failed' && (
                      <button className="icon-btn" onClick={() => handleEdit(req)}>
                        <i className="fas fa-edit" title="ویرایش و ارسال مجدد" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {requests.length === 0 && (
                <p className="empty-state">هیچ درخواستی ثبت نکرده‌اید</p>
              )}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <i className="fas fa-chevron-right" />
                </button>
                <span>صفحه {page} از {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <i className="fas fa-chevron-left" />
                </button>
              </div>
            )}
          </div>
        );

      case 'new-request':
        return (
          <div className="card glass pc-animate-fade-in form-wizard">
            {!enableTeacherRegistration ? (
              <div
                className="alert alert-warning"
                style={{
                  padding: '1rem',
                  background: '#fef3c7',
                  borderRadius: '8px',
                  color: '#92400e',
                }}
              >
                <i className="fas fa-exclamation-triangle" /> ثبت‌نام کلاس جدید توسط ادمین غیرفعال شده است. لطفاً بعداً تلاش کنید.
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fas fa-graduation-cap" /> پایه تحصیلی</label>
                    <select name="grade" value={form.grade} onChange={handleInput}>
                      {gradeOptions
                        .filter((g) => g !== 'همه پایه‌ها')
                        .map((g) => <option key={g}>{g}</option>)}
                    </select>
                  </div>
                  {needsField(form.grade) && (
                    <div className="form-group">
                      <label><i className="fas fa-layer-group" /> رشته</label>
                      <select name="field" value={form.field} onChange={handleInput}>
                        {availableFields.map((f) => <option key={f}>{f}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label><i className="fas fa-book" /> درس</label>
                  <select name="subject" value={form.subject} onChange={handleInput} required>
                    <option value="">انتخاب درس...</option>
                    {availableSubjects.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label><i className="fas fa-heading" /> عنوان کلاس</label>
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleInput}
                    required
                    placeholder="مثال: جمع‌بندی ریاضی کنکور"
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-align-left" /> توضیحات (اختیاری)</label>
                  <textarea
                    name="description"
                    rows="3"
                    value={form.description}
                    onChange={handleInput}
                    placeholder="توضیحات تکمیلی..."
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fas fa-calendar-day" /> تاریخ (شمسی)</label>
                    <PersianDatePicker
                      value={form.date}
                      onChange={(newDate) => setForm((p) => ({ ...p, date: newDate }))}
                    />
                  </div>
                  <div className="form-group">
                    <label><i className="fas fa-clock" /> ساعت</label>
                    <input
                      type="time"
                      name="time"
                      value={form.time}
                      onChange={handleInput}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label><i className="fas fa-hourglass" /> مدت زمان (دقیقه)</label>
                    <input
                      type="number"
                      name="duration"
                      value={form.duration}
                      onChange={handleInput}
                      min="10"
                      max="300"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label><i className="fas fa-tag" /> قیمت (تومان)</label>
                    <input
                      type="number"
                      name="price"
                      value={form.price}
                      onChange={handleInput}
                      required
                      min="1000"
                      step="1000"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label><i className="fas fa-users" /> ظرفیت</label>
                  <select name="capacity" value={form.capacity} onChange={handleInput}>
                    {capacityOptions.map((n) => <option key={n}>{n}</option>)}
                  </select>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-outline"
                    onClick={() => {
                      setEditingRequest(null);
                      setForm({ ...DEFAULT_TEACHER_REQUEST_FORM });
                    }}
                  >
                    لغو
                  </button>
                  <button
                    type="submit"
                    className="btn-primary gradient-animate"
                    disabled={isSubmitting}
                  >
                    <i className="fas fa-paper-plane" />{' '}
                    {isSubmitting
                      ? 'در حال ارسال...'
                      : editingRequest
                      ? 'ویرایش'
                      : 'ارسال درخواست'}
                  </button>
                </div>
              </form>
            )}
          </div>
        );

      case 'my-classes':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-book-open" /> کلاس‌های من</h3>
            </div>
            {teacherCourses.length === 0 ? (
              <p className="empty-state">هیچ کلاسی ندارید</p>
            ) : (
              teacherCourses.slice().reverse().map((c) => {
                const cancelled = c.status === COURSE_STATUS.CANCELLED;
                return (
                  <div
                    key={c.id}
                    className="enrolled-item"
                    style={{ opacity: cancelled ? 0.6 : 1 }}
                  >
                    <div>
                      <strong>{c.title}</strong>
                      <br />
                      <span className="meta-text">
                        <i className="fas fa-user" /> {c.teacher} –{' '}
                        <i className="fas fa-calendar-alt" /> {c.date} –{' '}
                        <i className="fas fa-clock" /> {c.time} – مدت:{' '}
                        {c.duration || 60} دقیقه
                      </span>
                      <br />
                      <span className="meta-text">
                        وضعیت: {getCourseStatusLabel(c, nowMoment)}
                      </span>
                      <br />
                      <span className="meta-text">
                        <i className="fas fa-user" /> {c.enrolled}/{c.capacity} دانش‌آموز
                      </span>
                      {c.cancelReason && (
                        <>
                          <br />
                          <span className="meta-text" style={{ color: '#f43f5e' }}>
                            دلیل لغو: {c.cancelReason}
                          </span>
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      {renderCourseActions(c)}
                      <button
                        className="btn-outline btn-sm"
                        onClick={() => {
                          setSelectedCourseStudents(c.enrolledStudents || []);
                          setSelectedCourseTitle(c.title);
                          setShowStudentsModal(true);
                        }}
                      >
                        <i className="fas fa-users" /> دانش‌آموزان ({c.enrolled})
                      </button>
                      <button
                        className="btn-outline btn-sm"
                        onClick={() => {
                          setSelectedCourse(c);
                          setModalOpen(true);
                        }}
                      >
                        <i className="fas fa-info-circle" /> اطلاعات
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );

      case 'calendar':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-calendar-alt" /> تقویم شمسی</h3>
            </div>
            <PersianCalendar
              courses={teacherCourses}
              onDateClick={(date) => showToast(`نمایش کلاس‌های ${date}`, 'info')}
            />
          </div>
        );

      case 'wallet':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-wallet" /> کیف پول</h3>
            </div>
<div className="wallet-grid">
  <div className="wallet-box gradient-box">
    <div className="wallet-label">موجودی قابل برداشت</div>
    <div className="wallet-amount">
      {(userData?.wallet || 0).toLocaleString('fa-IR')} <span>تومان</span>
    </div>
  </div>
  <div className="wallet-box gradient-box secondary">
    <div className="wallet-label">در انتظار قفل‌شده (خالص)</div>
    <div className="wallet-amount">
      {(userData?.pendingAmount || 0).toLocaleString('fa-IR')} <span>تومان</span>
    </div>
  </div>
</div>

{/* ✅ باکس اطلاع‌رسانی ساختار درآمد */}
<div style={{
  marginTop: '1rem',
  padding: '1rem',
  background: 'rgba(251, 191, 36, 0.08)',
  border: '1px solid rgba(251, 191, 36, 0.3)',
  borderRadius: '12px',
  fontSize: '0.9rem',
  lineHeight: '1.8',
  marginBottom:"20px" 
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
    <i className="fas fa-percentage" style={{ color: '#fbbf24', fontSize: '1.2rem' }} />
    <strong>ساختار درآمد شما:</strong>
  </div>
  <div style={{ paddingRight: '1.7rem', color: 'var(--pc-text-muted)', fontSize: '0.85rem'}}>
    • <strong>قیمت پایه کلاس:</strong> همان مبلغی که تعیین می‌کنید<br />
    • <strong>کارمزد سکو:</strong> {state.adminSettings.commission || 0}٪ از قیمت پایه<br />
    {/*• <strong>مالیات ارزش افزوده ({VAT_RATE}٪):</strong> جداگانه توسط دانش‌آموز پرداخت می‌شود<br />
    • <strong style={{ color: '#22c55e' }}>دریافتی شما از هر ثبت‌نام:</strong> قیمت پایه − {state.adminSettings.commission || 0}٪*/}
  </div>
</div>
<button className="btn-primary btn-glow" onClick={() => setWithdrawModalOpen(true)}>
  <i className="fas fa-hand-holding-usd" /> درخواست برداشت
</button>
            <div style={{ marginTop: '2rem' }}>
              <h4>تاریخچه تراکنش‌ها</h4>
              {state.transactions.filter((t) => t.userId === user.id).length === 0 ? (
                <p className="empty-state">هیچ تراکنشی ثبت نشده است</p>
              ) : (
                <table className="transaction-table">
                  <thead>
                    <tr><th>تاریخ</th><th>مبلغ</th><th>نوع</th></tr>
                  </thead>
                  <tbody>
                    {state.transactions
                      .filter((t) => t.userId === user.id)
                      .slice()
                      .reverse()
                      .slice(0, 10)
                      .map((t) => (
                        <tr key={t.id}>
                          <td>{t.date}</td>
                          <td><strong>{t.amount.toLocaleString('fa-IR')} تومان</strong></td>
                          <td>
                            {t.type === 'enroll' ? 'ثبت‌نام'
                              : t.type === 'deposit' ? 'شارژ'
                              : t.type === 'withdraw' ? 'برداشت'
                              : t.type === 'refund' ? 'بازگشت وجه'
                              : t.type === 'payment_release' ? 'واریز از برگزاری'
                              : t.type === 'pending_payment' ? 'در انتظار واریز'
                              : t.type}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
                  <WithdrawModal
        isOpen={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        maxWithdrawToman={userData?.wallet || 0}
        onWithdraw={handleWithdrawSubmit}
        initialBankingInfo={userData?.bankingInfo || {}}
      />
          </div>
        );

              case 'withdrawals': {
        const myWithdrawals = state.transactions
          .filter(t => t.userId === user.id && t.type === 'withdraw' && !t.deletedByTeacher)
          .slice()
          .reverse();

        const pendingList = myWithdrawals.filter(w => w.status === 'pending');
        const historyList = myWithdrawals.filter(w => w.status !== 'pending');

        const renderItem = (w) => {
          const statusMap = {
            pending: { label: '⏳ در انتظار تایید', color: '#f59e0b', bg: '#fef3c7' },
            completed: { label: '✅ واریز شده', color: '#10b981', bg: '#d1fae5' },
            rejected: { label: '❌ رد شده', color: '#ef4444', bg: '#fee2e2' },
          };
          const st = statusMap[w.status] || statusMap.pending;

          return (
            <div
              key={w.id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1rem', borderRadius: '14px',
                background: 'rgba(255, 255, 255, 0.5)',
                border: `1.5px solid ${st.color}40`,
                marginBottom: '0.7rem', gap: '0.8rem', flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: '180px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#1f2937' }}>
                  {w.amount.toLocaleString('fa-IR')} تومان
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  📅 {w.date || '—'} | 💳 ****{String(w.cardNumber || '').slice(-4)}
                </div>
                {w.status === 'completed' && w.adminRef && (
                  <div style={{ fontSize: '0.78rem', color: '#10b981', marginTop: '0.3rem', fontWeight: 'bold' }}>
                    🔖 کد پیگیری: {w.adminRef}
                  </div>
                )}
                {w.status === 'rejected' && (
                  <div style={{ fontSize: '0.78rem', color: '#ef4444', marginTop: '0.3rem' }}>
                    💰 مبلغ به کیف پول شما بازگشت
                  </div>
                )}
              </div>

              <span style={{
                padding: '0.35rem 0.8rem', borderRadius: '20px',
                background: st.bg, color: st.color,
                fontSize: '0.8rem', fontWeight: 'bold', whiteSpace: 'nowrap',
              }}>
                {st.label}
              </span>

              {(w.status === 'completed' || w.status === 'rejected') && (
                <button
                  onClick={() => handleArchiveWithdrawal(w.id)}
                  title="انتقال به سطل زباله"
                  style={{
                    width: 38, height: 38, borderRadius: '50%',
                    border: 'none', background: '#fee2e2', color: '#ef4444',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: '1rem',
                  }}
                >
                  <i className="fas fa-trash" />
                </button>
              )}
            </div>
          );
        };

        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-hand-holding-usd" /> درخواست‌های برداشت من</h3>
            </div>

            {pendingList.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.95rem', color: '#f59e0b', marginBottom: '0.7rem', fontWeight: 'bold' }}>
                  <i className="fas fa-clock" /> در انتظار بررسی ({pendingList.length})
                </h4>
                {pendingList.map(renderItem)}
              </div>
            )}

            {historyList.length > 0 && (
              <div>
                <h4 style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: '0.7rem', fontWeight: 'bold' }}>
                  <i className="fas fa-history" /> تاریخچه ({historyList.length})
                </h4>
                {historyList.map(renderItem)}
              </div>
            )}

            {myWithdrawals.length === 0 && (
              <div style={{ padding: '3rem 1rem', textAlign: 'center', color: '#94a3b8' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '1rem', display: 'block' }} />
                <p>هنوز هیچ درخواست برداشتی ثبت نکرده‌اید</p>
                <button
                  className="btn-primary btn-sm"
                  style={{ marginTop: '1rem' }}
                  onClick={() => { setActiveTab('wallet'); }}
                >
                  <i className="fas fa-plus" /> رفتن به کیف پول
                </button>
              </div>
            )}
          </div>
        );
      }

      

      default:
        return null;
    }
  };

  // ============================================================
  // رندر نهایی
  // ============================================================
  return (
    <div className="panel-wrapper">
      <div className="pc-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`fas ${tab.icon}`} /> {tab.label}
          </button>
        ))}
      </div>
      {renderContent()}

      {/* مودال اطلاعات کلاس */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedCourse?.title}
      >
        {selectedCourse && (
          <>
            <p><strong>معلم:</strong> {selectedCourse.teacher}</p>
            <p><strong>تاریخ:</strong> {selectedCourse.date} – {selectedCourse.time}</p>
            <p><strong>مدت زمان:</strong> {selectedCourse.duration || 60} دقیقه</p>
            {selectedCourse.price > 0 && (
  <>
    <p><strong>قیمت پایه:</strong> {selectedCourse.price.toLocaleString('fa-IR')} تومان</p>
    <p style={{ fontSize: '0.9rem' }}>
      <strong>کارمزد سکو ({state.adminSettings.commission || 0}٪):</strong>{' '}
      {Math.round(selectedCourse.price * (state.adminSettings.commission || 0) / 100).toLocaleString('fa-IR')} تومان
    </p>
    <p style={{ fontSize: '0.9rem', color: '#22c55e', fontWeight: 'bold' }}>
      <strong>دریافتی شما از هر ثبت‌نام:</strong>{' '}
      {(
        selectedCourse.price -
        Math.round(selectedCourse.price * (state.adminSettings.commission || 0) / 100)
      ).toLocaleString('fa-IR')} تومان
    </p>
    <p style={{ fontSize: '0.8rem', color: 'var(--pc-text-muted)' }}>
      <i className="fas fa-info-circle" /> مالیات ({VAT_RATE}٪) جداگانه توسط دانش‌آموز پرداخت می‌شود.
    </p>
  </>
)}
            <p><strong>ظرفیت:</strong> {selectedCourse.enrolled}/{selectedCourse.capacity}</p>
            <p><strong>وضعیت:</strong> {getCourseStatusLabel(selectedCourse, nowMoment)}</p>
            {selectedCourse.startedAt && (
              <p><strong>شروع در:</strong> {selectedCourse.startedAt}</p>
            )}
            {selectedCourse.endedAt && (
              <p><strong>پایان در:</strong> {selectedCourse.endedAt}</p>
            )}
            {selectedCourse.cancelReason && (
              <p><strong>دلیل لغو:</strong> {selectedCourse.cancelReason}</p>
            )}
            <RatingStars rating={selectedCourse.rating || 0} readonly />
            {selectedCourse.status === COURSE_STATUS.LIVE && (
              <button
                className="btn-primary"
                onClick={() => handleJoinClass(selectedCourse.id)}
                style={{ marginTop: '0.5rem' }}
              >
                <i className="fas fa-video" /> ادامه کلاس
              </button>
            )}
            {selectedCourse.status === COURSE_STATUS.SCHEDULED &&
              canStartCourse(selectedCourse, nowMoment) && (
                <button
                  className="btn-primary"
                  onClick={() => {
                    setModalOpen(false);
                    handleStartClass(selectedCourse.id);
                  }}
                  disabled={isStartingClass}
                  style={{ marginTop: '0.5rem' }}
                >
                  <i className="fas fa-play" /> شروع کلاس
                </button>
              )}
          </>
        )}
      </Modal>

      {/* مودال لیست دانش‌آموزان */}
      <Modal
        isOpen={showStudentsModal}
        onClose={() => setShowStudentsModal(false)}
        title={`لیست دانش‌آموزان کلاس ${selectedCourseTitle}`}
      >
        {selectedCourseStudents.length === 0 ? (
          <p className="empty-state">هیچ دانش‌آموزی ثبت‌نام نکرده است</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {selectedCourseStudents.map((s, idx) => (
              <li
                key={idx}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--pc-border-color)',
                }}
              >
                <strong>{s.fullName}</strong> – {s.phone}
                <span style={{ fontSize: '0.8rem', color: 'var(--pc-text-muted)' }}>
                  {' '}(ثبت‌نام در {s.enrolledAt})
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="form-actions">
          <button className="btn-outline" onClick={() => setShowStudentsModal(false)}>
            بستن
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ================================================================
// کامپوننت اصلی
// ================================================================
function TeacherPrivateClassPageContent() {
  const { user, updateUser } = useAuth();
  const { state, dispatch, refreshData, internalApi, roomeetApi, loading, error } = useData();
  const showToast = useToast();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // ✅ E26: پاس‌دادن users به authGate
  const authGate = useRoomeetAuthGate({
    user,
    updateUser,
    roomeetApi,
    requiredRole: 'teacher',
    users: state.users,
  });
  const isRoomeetReady = authGate.isReady;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim().length < 2) {
        setSearchResults([]);
        setIsSearchOpen(false);
        return;
      }
      const courses = await internalApi.getCourses();
      const results = courses.filter(
        (c) =>
          c.title?.includes(query) ||
          c.teacher?.includes(query) ||
          c.subject?.includes(query) ||
          c.grade?.includes(query) ||
          c.field?.includes(query) ||
          c.description?.includes(query)
      );
      setSearchResults(results);
      setIsSearchOpen(true);
    },
    [internalApi]
  );

  useSearchShortcut(inputRef);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { scrollProgress, headerScrolled } = useScrollProgress();

  if (!user) {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div className="card glass" style={{ maxWidth: '500px', margin: '0 auto', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              لطفاً ابتدا وارد شوید
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              برای دسترسی به این صفحه، باید وارد حساب کاربری خود شوید.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/login')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> رفتن به صفحه ورود
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'teacher') {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div
            className="card glass"
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              padding: '3rem 2rem',
              borderColor: '#f43f5e',
            }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              دسترسی غیرمجاز
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              این صفحه مخصوص معلمان است. لطفاً با نقش مناسب وارد شوید.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/home')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> بازگشت به خانه
            </button>
          </div>
        </div>
      </div>
    );
  }

  const notifCount = state.notifications.filter(
    (n) => !n.read && (!n.userId || n.userId === user.id)
  ).length;
  const panelProps = {
    user,
    state,
    dispatch,
    refreshData,
    showToast,
    internalApi,
    roomeetApi,
    loading,
    isRoomeetReady,
  };

  return (
    <AuthGate
      status={authGate.status}
      roleLabel="معلم"
      roleIcon="fa-chalkboard-teacher"
      requiredRole="teacher"
      loginError={authGate.loginError}
      onLogin={authGate.login}
    >
      <div className="private-class-page">
        <div className="scroll-progress-bar" style={{ width: scrollProgress + '%' }} />
        <PageParticles />
        <header className={`pc-header ${headerScrolled ? 'scrolled' : ''}`}>
          <div className="header-left">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <i className="fas fa-chalkboard-teacher" />
                <div className="logo-pulse" />
              </div>
              <h1>
                کلاس خصوصی<span className="role-badge">معلم</span>
              </h1>
            </div>
            <div className="header-subtitle">
              <span className="subtitle-text">🎯 مدیریت کلاس‌های خود</span>
            </div>
          </div>
          <div className="header-right">
            <div className="search-box-wrapper" ref={searchRef}>
              <div className={`search-box ${isSearchOpen && searchResults.length > 0 ? 'active' : ''}`}>
                <i className="fas fa-search" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="جستجوی کلاس..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.trim().length >= 2 && setIsSearchOpen(true)}
                />
                <span className="type-cursor" />
                <span className="search-shortcut">⌘K</span>
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setIsSearchOpen(false);
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              {isSearchOpen && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.slice(0, 6).map((c) => (
                    <div
                      key={c.id}
                      className="search-result-item"
                      onClick={() => {
                        setIsSearchOpen(false);
                        showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «درخواست‌ها» یا «کلاس‌های من» بروید`, 'info');
                      }}
                    >
                      <div className="result-icon" style={{ background: c.color || '#0b2b5c' }}>
                        <i className={`fas ${c.icon || 'fa-chalkboard'}`} />
                      </div>
                      <div className="result-info">
                        <div className="result-title">{c.title}</div>
                        <div className="result-meta">
                          {c.teacher} • {(c.price || 0).toLocaleString('fa-IR')} تومان
                        </div>
                      </div>
                      <button
                        className="result-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSearchOpen(false);
                          showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «درخواست‌ها» یا «کلاس‌های من» بروید`, 'info');
                        }}
                      >
                        مشاهده
                      </button>
                    </div>
                  ))}
                  {searchResults.length > 6 && (
                    <div className="search-more">+ {searchResults.length - 6} نتیجه دیگر</div>
                  )}
                </div>
              )}
            </div>
            {/*<button className="theme-toggle" onClick={toggleTheme} aria-label="تغییر تم">
              <span className="icon">{theme === 'light' ? '🌙' : '☀️'}</span>
            </button>
            <div
              className="notification-wrapper"
              onClick={() => showToast(`${notifCount} نوتیفیکیشن جدید`, 'info')}
              aria-label="اعلان‌ها"
            >
              <i className="fas fa-bell">
                {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
              </i>
              <div className="notification-dot" />
            </div>
            <div className="avatar-wrapper">
              <img
                src={`https://ui-avatars.com/api/?name=${(user.name || 'کاربر').replace(/ /g, '+')}&background=0b2b5c&color=fff&size=46`}
                alt="آواتار"
                loading="lazy"
              />
              <div className="avatar-ring" />
              <div className="avatar-status" />
            </div>*/}
          </div>
        </header>
        <div className="pc-content">
          {error && (
            <div className="card glass" style={{ borderColor: '#f43f5e', marginBottom: '1.5rem' }}>
              <p style={{ color: '#f43f5e', fontWeight: 'bold' }}>
                ⚠️ خطا در بارگذاری داده‌ها: {error}
              </p>
              <button className="btn-primary btn-sm" onClick={() => refreshData()}>
                تلاش مجدد
              </button>
            </div>
          )}
          {loading ? (
            <div className="skeleton-card" style={{ height: '300px', borderRadius: '28px' }} />
          ) : (
            <TeacherPanel {...panelProps} />
          )}
        </div>
      </div>
    </AuthGate>
  );
}

const MemoizedTeacherPrivateClassPageContent = React.memo(TeacherPrivateClassPageContent);

export default function TeacherPrivateClassPage() {
  return (
    <PrivateClassProviders>
      <MemoizedTeacherPrivateClassPageContent />
    </PrivateClassProviders>
  );
}